import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Rocket,
  Send,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import type { OnMount } from "@monaco-editor/react";

import { AntiCheatModal } from "@/components/test/AntiCheatModal";
import { CandidateCard } from "@/components/test/CandidateCard";
import { DocsPanel } from "@/components/test/DocsPanel";
import { EditorWindow } from "@/components/test/EditorWindow";
import { InstructionsModal } from "@/components/test/InstructionsModal";
import { ResultsPanel } from "@/components/test/ResultsPanel";
import { TestHeader } from "@/components/test/TestHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBanner } from "@/components/ui/status-banner";
import {
  useAntiCheat,
  violationLabel,
  requestFullscreen,
  exitFullscreen,
  type AntiCheatViolation,
} from "@/hooks/useAntiCheat";
import { useCountdown } from "@/hooks/useCountdown";
import { sendConfirmation } from "@/lib/invitations";
import { runTsExercise } from "@/lib/runtimes/ts";
import { runSqlExercise } from "@/lib/runtimes/sql";
import type { RunResult } from "@/lib/runtimes/types";
import { supabase } from "@/lib/supabase";
import type { Assignment, Exercise, Language, Violation } from "@/lib/types";

export const Route = createFileRoute("/test/$assignmentId")({
  component: TestPage,
});

type Monaco = Parameters<OnMount>[1];

interface MonacoCtx {
  monaco: Monaco;
  modelUri: string;
}


function TestPage() {
  const { assignmentId } = Route.useParams();
  return <TestController assignmentId={assignmentId} />;
}

// =================== CONTROLLER ===================
function TestController({ assignmentId }: { assignmentId: string }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: aData, error: aErr } = await supabase
          .from("assignments")
          .select("*")
          .eq("id", assignmentId)
          .single();
        if (cancelled) return;
        if (aErr || !aData) throw new Error("Invitación no encontrada.");
        const a = aData as Assignment;

        if (!a.exercise_ids || a.exercise_ids.length === 0) {
          throw new Error("Esta invitación no tiene ejercicios.");
        }

        // Cargamos TODOS los ejercicios y los ordenamos según exercise_ids.
        const { data: eData, error: eErr } = await supabase
          .from("exercises")
          .select("*")
          .in("id", a.exercise_ids);
        if (cancelled) return;
        if (eErr || !eData || eData.length === 0) {
          throw new Error("No se encontraron los ejercicios.");
        }
        const byId = new Map(eData.map((e) => [e.id, e as Exercise]));
        const ordered = a.exercise_ids
          .map((id) => byId.get(id))
          .filter((e): e is Exercise => !!e);
        if (ordered.length !== a.exercise_ids.length) {
          // Algún ejercicio del array no existe (fue borrado). Igual seguimos
          // con los que están.
          console.warn(
            `Solo se cargaron ${ordered.length}/${a.exercise_ids.length} ejercicios.`
          );
        }

        setAssignment(a);
        setExercises(ordered);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignmentId]);

  if (loading) {
    return (
      <SplashScreen
        icon={<Loader2 className="h-5 w-5 animate-spin" />}
        message="Cargando invitación…"
      />
    );
  }
  if (error || !assignment || exercises.length === 0) {
    return (
      <SplashScreen
        icon={<XCircle className="h-5 w-5 text-destructive" />}
        title="Invitación inválida"
        message={error ?? "No se pudo cargar la invitación."}
      />
    );
  }
  if (assignment.invalidated_at) {
    return (
      <SplashScreen
        icon={<ShieldAlert className="h-6 w-6 text-destructive" />}
        title="Test invalidado"
        message={
          assignment.invalidation_reason ??
          "Tu test fue marcado como invalidado por violación de reglas."
        }
        accent="error"
      />
    );
  }
  if (assignment.completed_at) {
    return (
      <SplashScreen
        icon={<CheckCircle2 className="h-6 w-6 text-success" />}
        title="Ya completaste este test"
        message="Recibimos tu solución. El evaluador va a revisarla y se va a contactar."
        accent="success"
      />
    );
  }
  if (
    assignment.expires_at &&
    new Date(assignment.expires_at) < new Date() &&
    !assignment.started_at
  ) {
    return (
      <SplashScreen
        icon={<AlertTriangle className="h-6 w-6 text-warning" />}
        title="Esta invitación venció"
        message="Contactá al evaluador para que te mande una nueva invitación."
        accent="warning"
      />
    );
  }

  return <TestWorkspace assignment={assignment} exercises={exercises} />;
}

// =================== SPLASH ===================
function SplashScreen({
  icon,
  title,
  message,
  accent = "info",
}: {
  icon: React.ReactNode;
  title?: string;
  message: string;
  accent?: "info" | "success" | "warning" | "error";
}) {
  const ring = {
    info: "bg-primary/10 border-primary/30",
    success: "bg-success/10 border-success/30",
    warning: "bg-warning/10 border-warning/30",
    error: "bg-destructive/10 border-destructive/30",
  }[accent];
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-10 text-center">
        <div
          className={`inline-flex h-12 w-12 items-center justify-center rounded-full border mb-4 ${ring}`}
        >
          {icon}
        </div>
        {title && (
          <h1 className="text-xl font-semibold tracking-tight mb-2">{title}</h1>
        )}
        <p className="text-sm text-muted-foreground">{message}</p>
      </Card>
    </div>
  );
}

// =================== WORKSPACE ===================
function TestWorkspace({
  assignment,
  exercises,
}: {
  assignment: Assignment;
  exercises: Exercise[];
}) {
  // === Estado multi-ejercicio ===
  const [currentIdx, setCurrentIdx] = useState(
    Math.min(assignment.current_exercise_index ?? 0, exercises.length - 1)
  );
  const currentExercise = exercises[currentIdx];
  const isLastExercise = currentIdx >= exercises.length - 1;
  const totalExercises = exercises.length;

  // === Estado del editor (resetea entre ejercicios) ===
  const [code, setCode] = useState(currentExercise.starter_code);
  // Instrucciones NO se ven hasta arrancar. Al avanzar a un nuevo ejercicio
  // las abrimos automáticamente para mostrar la consigna del ejercicio nuevo.
  const [showInstructions, setShowInstructions] = useState(false);
  const [showAntiCheat, setShowAntiCheat] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [markers, setMarkers] = useState({ errors: 0, warnings: 0 });
  const [attempts, setAttempts] = useState(0);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const [violations, setViolations] = useState<Violation[]>(
    assignment.violations ?? []
  );
  const [violationWarning, setViolationWarning] = useState<string | null>(null);
  // Started_at del EJERCICIO actual (no del assignment entero).
  const [exerciseStartedAt, setExerciseStartedAt] = useState<number | null>(
    assignment.current_exercise_started_at
      ? new Date(assignment.current_exercise_started_at).getTime()
      : null
  );
  const [fullscreen, setFullscreen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [terminal, setTerminal] = useState<
    | { kind: "completed" }
    | { kind: "invalidated"; reason: string }
    | { kind: "timed_out" }
    | null
  >(null);

  const inProgress = !!exerciseStartedAt && !terminal;
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  const monacoCtxRef = useRef<MonacoCtx | null>(null);
  const codeRef = useRef(code);
  codeRef.current = code;

  useEffect(() => {
    function onChange() {
      setFullscreen(!!document.fullscreenElement);
    }
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // === Countdown del ejercicio actual ===
  const deadline = useMemo(() => {
    if (!exerciseStartedAt) return null;
    return exerciseStartedAt + currentExercise.duration_min * 60 * 1000;
  }, [exerciseStartedAt, currentExercise.duration_min]);

  const { remainingSec } = useCountdown({
    deadline,
    onExpired: () => {
      if (!terminal) finalize("timed_out");
    },
  });

  // === Anti-cheat ===
  useAntiCheat({
    enabled: inProgress,
    onViolation: handleViolation,
  });

  async function handleViolation(v: AntiCheatViolation) {
    const newViolation: Violation = { kind: v, at: new Date().toISOString() };
    const newList = [...violations, newViolation];
    setViolations(newList);
    await supabase
      .from("assignments")
      .update({
        violations: newList,
        violation_count: newList.length,
      })
      .eq("id", assignment.id);

    if (newList.length === 1) {
      setViolationWarning(violationLabel(v));
      return;
    }
    finalize("invalidated", `${violationLabel(v)} (2da violación)`);
  }

  // === Acciones ===
  async function startTest() {
    try {
      await requestFullscreen();
    } catch {
      // si falla fullscreen, seguimos (no es violación)
    }
    const now = new Date().toISOString();
    const update: Record<string, unknown> = {
      current_exercise_index: currentIdx,
      current_exercise_started_at: now,
    };
    // started_at del assignment se setea solo la primera vez
    if (!assignment.started_at) update.started_at = now;
    const { error } = await supabase
      .from("assignments")
      .update(update)
      .eq("id", assignment.id);
    if (error) {
      alert("No se pudo iniciar: " + error.message);
      return;
    }
    setExerciseStartedAt(Date.now());
    // Abrir instrucciones del primer ejercicio (recién acá las puede ver).
    setShowInstructions(true);
  }

  /**
   * Avanza al próximo ejercicio o termina el assignment si es el último.
   * Se llama después de cada submit final (manual o por timeout).
   */
  async function advanceOrComplete() {
    if (isLastExercise) {
      // Último ejercicio: marcar assignment como completed + email final
      await supabase
        .from("assignments")
        .update({ completed_at: new Date().toISOString() })
        .eq("id", assignment.id);
      await sendConfirmation(assignment.id);
      await exitFullscreen();
      setTerminal({ kind: "completed" });
      return;
    }

    // Hay siguiente: avanzamos
    const nextIdx = currentIdx + 1;
    const nextStartedAt = new Date().toISOString();
    const { error: advanceErr } = await supabase
      .from("assignments")
      .update({
        current_exercise_index: nextIdx,
        current_exercise_started_at: nextStartedAt,
      })
      .eq("id", assignment.id);
    if (advanceErr) {
      // Si el DB rechaza el avance (RLS o columna faltante), no movemos el
      // state local: el candidato sigue en el mismo ejercicio en vez de
      // perderse en uno que el evaluador nunca vea.
      alert(
        `No se pudo avanzar al siguiente ejercicio: ${advanceErr.message}. ` +
          `Tu última entrega quedó guardada. Recargá la página o avisá al evaluador.`
      );
      return;
    }

    // Reset state local para el nuevo ejercicio
    setCurrentIdx(nextIdx);
    setCode(exercises[nextIdx].starter_code);
    setAttempts(0);
    setResult(null);
    setMarkers({ errors: 0, warnings: 0 });
    setExerciseStartedAt(Date.now());
    // Abrir instrucciones del nuevo ejercicio
    setShowInstructions(true);
  }

  async function toggleFullscreen() {
    if (document.fullscreenElement) await exitFullscreen();
    else await requestFullscreen();
  }

  async function runAndScore(): Promise<RunResult | null> {
    if (currentExercise.language === "sql") {
      return await runSqlExercise({
        code: codeRef.current,
        exerciseSetupSql: currentExercise.setup_sql,
        exerciseSqlTests: currentExercise.sql_tests,
      });
    }
    if (!monacoCtxRef.current) {
      return {
        results: [],
        passedCount: 0,
        totalCount: 0,
        score: 0,
        fatalError: "El editor todavía no está listo.",
      };
    }
    return await runTsExercise({
      code: codeRef.current,
      exerciseTestsCode: currentExercise.tests_code,
      monaco: monacoCtxRef.current.monaco,
      modelUri: monacoCtxRef.current.modelUri,
    });
  }

  async function persistSubmission(
    r: RunResult,
    attemptN: number,
    isFinal: boolean
  ) {
    await supabase.from("submissions").insert({
      assignment_id: assignment.id,
      exercise_id: currentExercise.id,
      attempt_n: attemptN,
      code: codeRef.current,
      test_results: r.results,
      passed_count: r.passedCount,
      total_count: r.totalCount,
      score: r.score,
      is_final: isFinal,
    });
  }

  /** Botón "Comenzar Ejercicio" → modal anti-cheat. */
  function startAction() {
    setShowAntiCheat(true);
  }

  /** Botón "Testear" → corre tests sin marcar como final. */
  async function runTests() {
    if (!inProgress || running || finishing) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await runAndScore();
      if (r) {
        const newAttempt = attempts + 1;
        setAttempts(newAttempt);
        setResult(r);
        await persistSubmission(r, newAttempt, false);
      }
    } finally {
      setRunning(false);
    }
  }

  /**
   * Botón "Enviar a evaluador" → corre tests, guarda como final del ejercicio
   * actual, y avanza al próximo (o termina si es el último).
   */
  async function confirmSubmitFinal() {
    if (running || finishing) return;
    setShowConfirmSubmit(false);
    setFinishing(true);
    try {
      const r = await runAndScore();
      if (r) {
        const newAttempt = attempts + 1;
        setAttempts(newAttempt);
        setResult(r);
        await persistSubmission(r, newAttempt, true);
      }
      await advanceOrComplete();
    } finally {
      setFinishing(false);
    }
  }

  /**
   * Llamado en timeout (auto-submit del ejercicio) o invalidación (anti-cheat).
   * - "timed_out": guarda la submission final del ejercicio actual y avanza.
   * - "invalidated": invalida TODO el assignment (anti-cheat global).
   */
  async function finalize(
    kind: "invalidated" | "timed_out",
    reason?: string
  ) {
    if (finishing || terminal) return;
    setFinishing(true);
    try {
      const r = await runAndScore();
      if (r) {
        await persistSubmission(r, attempts + 1, true);
      }

      if (kind === "invalidated") {
        // Anti-cheat global: termina el assignment entero
        await supabase
          .from("assignments")
          .update({
            invalidated_at: new Date().toISOString(),
            invalidation_reason: reason ?? "Violación de reglas anti-cheat",
          })
          .eq("id", assignment.id);
        await sendConfirmation(assignment.id);
        await exitFullscreen();
        setTerminal({
          kind: "invalidated",
          reason: reason ?? "Violación de reglas",
        });
        return;
      }

      // timed_out de ESTE ejercicio:
      //   - Si es el último, marca timed_out_at + completed_at en el assignment.
      //   - Si NO es el último, simplemente avanza al siguiente. El timeout
      //     individual queda implícito en la submission is_final=true del
      //     ejercicio (su created_at marca cuándo expiró).
      if (isLastExercise) {
        await supabase
          .from("assignments")
          .update({
            timed_out_at: new Date().toISOString(),
            completed_at: new Date().toISOString(),
          })
          .eq("id", assignment.id);
        await sendConfirmation(assignment.id);
        await exitFullscreen();
        setTerminal({ kind: "timed_out" });
      } else {
        await advanceOrComplete();
      }
    } finally {
      setFinishing(false);
    }
  }

  function onMonacoMount(
    editor: Parameters<OnMount>[0],
    monaco: Parameters<OnMount>[1]
  ) {
    monacoCtxRef.current = {
      monaco,
      modelUri: editor.getModel()?.uri.toString() ?? "",
    };
  }

  function resetCode() {
    if (
      confirm("¿Volver al código inicial? Se va a perder lo que escribiste.")
    ) {
      setCode(currentExercise.starter_code);
    }
  }

  function clearResults() {
    setResult(null);
  }

  // === Terminal screens ===
  if (terminal?.kind === "completed") {
    return (
      <SplashScreen
        icon={<CheckCircle2 className="h-6 w-6 text-success" />}
        title="¡Gracias por tu tiempo!"
        message="Recibimos tu solución correctamente. El evaluador va a revisarla y se va a contactar. Te mandamos un email de confirmación a tu casilla."
        accent="success"
      />
    );
  }
  if (terminal?.kind === "invalidated") {
    return (
      <SplashScreen
        icon={<ShieldAlert className="h-6 w-6 text-destructive" />}
        title="Test invalidado"
        message={terminal.reason}
        accent="error"
      />
    );
  }
  if (terminal?.kind === "timed_out") {
    return (
      <SplashScreen
        icon={<AlertTriangle className="h-6 w-6 text-warning" />}
        title="Se acabó el tiempo"
        message="Enviamos automáticamente al evaluador tu última versión del código junto con los resultados de los tests que llegaste a correr. Te mandamos un email de confirmación."
        accent="warning"
      />
    );
  }

  return (
    <div className="min-h-screen px-8 py-8 flex flex-col">
      <div className="max-w-[1600px] w-full mx-auto flex-1 flex flex-col">
        <TestHeader
          exerciseTitle={currentExercise.title}
          language={currentExercise.language}
          remainingSec={remainingSec}
          fullscreen={fullscreen}
          instructionsAvailable={inProgress}
          progress={
            totalExercises > 1
              ? { current: currentIdx + 1, total: totalExercises }
              : undefined
          }
          onInstructions={() => setShowInstructions(true)}
          onDocs={() => setShowDocs(true)}
          onToggleFullscreen={toggleFullscreen}
        />

        {violationWarning && (
          <div className="mb-6">
            <StatusBanner
              variant="warning"
              title="⚠ Última oportunidad"
              onDismiss={() => setViolationWarning(null)}
            >
              {violationWarning}. Si lo volvés a hacer, el test se invalida
              automáticamente.
            </StatusBanner>
          </div>
        )}

        {/* === GRID === */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 flex-1 min-h-0">
          {/* Columna izquierda */}
          <section className="flex flex-col min-h-0">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-xl font-semibold tracking-tight">
                Tu Implementación ({langLabel(currentExercise.language)})
              </h2>
              <div className="text-xs font-mono text-muted-foreground">
                Intentos:{" "}
                <span
                  className={
                    attempts > 0 ? "text-warning" : "text-foreground"
                  }
                >
                  {attempts}
                </span>
              </div>
            </div>

            <CandidateCard
              name={assignment.candidate_name}
              email={assignment.candidate_email}
            />

            <div className="mt-4 flex flex-col relative">
              <div className={!inProgress ? "select-none" : ""}>
                <div
                  className={
                    !inProgress
                      ? "filter blur-md pointer-events-none transition-[filter] duration-300"
                      : "transition-[filter] duration-300"
                  }
                  aria-hidden={!inProgress}
                >
                  <EditorWindow
                    filename={`solution.${currentExercise.language === "sql" ? "sql" : currentExercise.language === "typescript" ? "ts" : "js"}`}
                    language={currentExercise.language}
                    value={code}
                    onChange={setCode}
                    onMount={onMonacoMount}
                    markers={markers}
                    onMarkersChange={setMarkers}
                    onReset={resetCode}
                  />
                </div>

                {!inProgress && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="rounded-md border border-border bg-card/80 backdrop-blur px-5 py-3 text-center pointer-events-auto">
                      <p className="text-sm font-medium">
                        El código se desbloquea al comenzar el ejercicio
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Hacé click en <strong>Comenzar Ejercicio</strong> para arrancar.
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                💡 La validación TypeScript se muestra en tiempo real. Los
                resultados aparecen a la derecha cuando corras los tests.
              </p>
            </div>

            <div className="flex gap-3 mt-5">
              {!inProgress ? (
                <Button
                  variant="success"
                  size="lg"
                  onClick={startAction}
                  className="flex-1 h-12 text-base"
                >
                  <Rocket className="h-4 w-4" />
                  Comenzar Ejercicio
                </Button>
              ) : (
                <>
                  <Button
                    variant="success"
                    size="lg"
                    onClick={runTests}
                    loading={running}
                    disabled={finishing}
                    className="flex-1 h-12 text-base"
                  >
                    <Rocket className="h-4 w-4" />
                    Testear
                  </Button>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => setShowConfirmSubmit(true)}
                    disabled={running || finishing}
                    className="h-12 text-base px-6"
                  >
                    <Send className="h-4 w-4" />
                    Enviar al Evaluador
                  </Button>
                </>
              )}
              <Button
                variant="subtle"
                size="lg"
                onClick={clearResults}
                disabled={running || finishing}
                className="h-12"
              >
                Limpiar
              </Button>
            </div>
          </section>

          {/* Columna derecha: Resultados */}
          <section className="flex flex-col min-h-0">
            <h2 className="text-xl font-semibold tracking-tight mb-4">
              Resultados
            </h2>
            <div className="flex-1 min-h-0">
              <ResultsPanel
                state={
                  running || finishing
                    ? { kind: "running" }
                    : result
                      ? { kind: "done", result }
                      : { kind: "idle" }
                }
              />
            </div>
          </section>
        </div>
      </div>

      <InstructionsModal
        open={showInstructions}
        onClose={() => setShowInstructions(false)}
        exerciseTitle={currentExercise.title}
        descriptionHtml={currentExercise.description}
      />
      <AntiCheatModal
        open={showAntiCheat}
        onCancel={() => setShowAntiCheat(false)}
        onAccept={async () => {
          setShowAntiCheat(false);
          await startTest();
        }}
        durationMin={currentExercise.duration_min}
      />
      <DocsPanel open={showDocs} onClose={() => setShowDocs(false)} />

      {/* Confirm submit final */}
      <Dialog
        open={showConfirmSubmit}
        onOpenChange={(v) => !v && !finishing && setShowConfirmSubmit(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-4 w-4 text-primary" />
              Enviar al evaluador
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro? Esta es tu <strong>solución final</strong>. Una vez
              enviada no vas a poder modificar el código. El evaluador va a
              revisar lo que escribiste y los tests que pasaron.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="default"
              onClick={() => setShowConfirmSubmit(false)}
              disabled={finishing}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={confirmSubmitFinal}
              loading={finishing}
            >
              <Send className="h-4 w-4" />
              Sí, enviar como final
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function langLabel(l: Language): string {
  return l === "typescript"
    ? "TypeScript"
    : l === "javascript"
      ? "JavaScript"
      : "SQL";
}
